import { HttpParameterCodec } from '@angular/common/http';

/**
 * What a note's `IM` value actually is, so the Notes tab can render it.
 *
 * `IM` is changing meaning. It has always been inline base64 (in principle —
 * in practice it is null for every note today, because GetTemperatureData's
 * server-side Note model carries no image field at all). Once the CMS uploads
 * photos to blob storage and DataServices adds `Image` to that model, `IM`
 * becomes a BLOB KEY instead: a path like `notes/<id>.png`.
 *
 * Both shapes will exist during the cutover — old notes already in Cosmos may
 * still hold inline data while new ones carry keys — so this classifies rather
 * than assuming, and the caller renders each accordingly. Guessing wrong is not
 * cosmetic: feeding a blob key to `data:image/png;base64,` produces a broken
 * image with no error, which is exactly the silent failure this design flagged.
 *
 * PURE AND SEPARATE so the rule can be tested without a component, an HTTP
 * client or a sanitizer.
 */
export type NoteImageKind = 'none' | 'key' | 'inline';

/** Extensions a note photo can plausibly arrive as. */
const IMAGE_EXT = /\.(png|jpe?g|webp|gif)$/i;

/**
 * Classify an `IM` value.
 *
 * A KEY is recognised by shape rather than by length: it contains a path
 * separator or ends in an image extension. Base64 payloads contain neither —
 * the standard alphabet is [A-Za-z0-9+/=] and, while `/` IS in that alphabet, a
 * base64 string never ends in `.png` and never looks like `notes/<id>.png`. So
 * the extension test is the reliable half and the separator test only applies
 * when an extension is present.
 *
 * Anything else non-empty is treated as inline data, which keeps today's
 * behaviour exactly for any note that really does carry base64.
 */
export function classifyNoteImage(im: string | null | undefined): NoteImageKind {
  if (im == null) return 'none';
  const value = im.trim();
  if (value.length === 0) return 'none';
  if (IMAGE_EXT.test(value)) return 'key';
  return 'inline';
}

/** True when this value needs a SAS lookup before it can be rendered. */
export function isNoteImageKey(im: string | null | undefined): boolean {
  return classifyNoteImage(im) === 'key';
}

/**
 * The DataServices route that trades a note's blob key for a short-lived SAS URL.
 *
 * CONTRACT (settled 2026-09-08 with admin-cosmos, key-based):
 *
 *   GET {apiUrl}v1/projects/notes/image?key={IM}
 *   -> 200 {"url": "<sas url>"}
 *
 * The parameter is `key` — NOT `imageKey`, which is what this sent until
 * 2026-09-08. That mismatch was invisible: IM is null on every note, so the
 * resolver never fires, so nothing exercised the parameter name. It would have
 * surfaced only when the CMS shipped images, as "note images silently never
 * load" — the placeholder path, no error, nothing in the console, with the cause
 * two deploys behind. Caught because admin-cosmos wrote the concrete route into
 * a message rather than saying the route was live.
 *
 * The key IS the `IM` value, passed VERBATIM. Two shapes (DS contract 2026-11-04,
 * DS feature/device-note-images):
 *   device-notes/{customerId}/{deviceId}/{projectName}/{sha256}.png — device uploads
 *     (FIVE segments, DS bcb8dc2 / D1=A: the photo belongs to the customer whose
 *     cure it was; the 4-segment form never shipped and is invalid)
 *   {projectName}/notes/{noteId}.png — legacy, still accepted
 * Project names may contain '%', '#', '+', '&' and spaces, so the key is sent
 * through encodeURIComponent EXACTLY (see NoteKeyEncoder) — Angular's default
 * query codec leaves some reserved characters raw.
 * PNG only: the server refuses .jpg etc. with a 400. The classifier below still
 * calls a .jpg a key, on purpose — the server's 400 then surfaces it as a
 * visible "photo unavailable", where treating it as inline base64 would draw a
 * broken image with no error.
 *
 * ERRORS (same contract) — each is SHOWN, never swallowed to the placeholder:
 *   400 malformed/unsupported key · 401 bearer · 403 the photo belongs to
 *   another CUSTOMER's cure · 404 a legacy key naming no project · 503
 *   ownership could not be checked (fail closed). A missing blob still mints a SAS (200); the image GET
 *   then 404s, which the <img> error handler catches.
 *
 * Bearer-gated like the rest of DataServices; the interceptor attaches the token
 * by URL prefix, so nothing here does anything about auth.
 */
export const NOTE_IMAGE_SAS_ROUTE = 'v1/projects/notes/image';

/**
 * Query codec for the key: plain encodeURIComponent, as the DS contract asks.
 * Angular's default HttpUrlEncodingCodec un-escapes @ : $ , ; = ? / after
 * encoding, so a project name holding one of those would reach the server
 * differently from what DS specified.
 */
export class NoteKeyEncoder implements HttpParameterCodec {
  encodeKey(k: string): string { return encodeURIComponent(k); }
  encodeValue(v: string): string { return encodeURIComponent(v); }
  decodeKey(k: string): string { return decodeURIComponent(k); }
  decodeValue(v: string): string { return decodeURIComponent(v); }
}

/**
 * Read the SAS URL out of the response.
 *
 * PINNED to `{url: string}`. This briefly accepted several field names and a
 * bare string, because the route did not exist and the shape was a guess. The
 * contract is settled now, so the guesses are gone rather than left to be
 * mistaken for a real contract later.
 *
 * Still refuses anything that is not an http(s) URL — a note image is rendered
 * into an <img src>, and `javascript:` in that position is an XSS, not a broken
 * picture. An unusable response resolves to null and the caller shows the
 * placeholder.
 */
export function extractSasUrl(body: string | null | undefined): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body.trim()) as { url?: unknown };
    const url = parsed?.url;
    return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
  } catch {
    return null;
  }
}

/**
 * Why a note photo could not be shown. Every value is rendered as a visible
 * marker on the note; none of them falls back to the placeholder, because the
 * placeholder is what a note WITHOUT a photo looks like and a failed photo must
 * not be mistaken for one.
 */
export type NotePhotoFailure =
  | 'not-signed-in'     // 401
  | 'not-permitted'     // 403
  | 'bad-key'           // 400
  | 'not-found'         // 404: legacy key naming no project
  | 'check-unavailable' // 503
  | 'unreachable'       // status 0: network/CORS
  | 'server-error'      // any other non-2xx
  | 'bad-response'      // 200 without a usable {url}
  | 'load-failed';      // the image itself failed to load (404 BlobNotFound, expired SAS)

/** Map a SAS-route HTTP status to the failure the note shows. Pure. */
export function sasFailureForStatus(status: number): NotePhotoFailure {
  switch (status) {
    case 0: return 'unreachable';
    case 400: return 'bad-key';
    case 401: return 'not-signed-in';
    case 403: return 'not-permitted';
    case 404: return 'not-found';
    case 503: return 'check-unavailable';
    default: return 'server-error';
  }
}

/** Per-key state for the Notes tab. */
export type NotePhotoState =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'unavailable'; reason: NotePhotoFailure };
